import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { FirebaseDatabaseService } from '../services/firebaseDatabaseService';
import { useAuth } from './FirebaseAuthContext';
import { STORAGE_KEYS } from '../config/constants';

// Firebase-compatible GameSession interface
interface GameSession {
  sessionId: string;
  userId: string;
  gameType: string;
  startTime: string;
  endTime?: string;
  isActive: boolean;
  notes?: string;
  createdAt: string;
}

interface GameSessionContextType {
  currentSession: GameSession | null;
  isSessionActive: boolean;
  startSession: (gameType: string, notes?: string) => Promise<GameSession>;
  endSession: () => Promise<void>;
  refreshSession: () => Promise<void>;
  getUserSessionStats: () => Promise<any>;
}

const GameSessionContext = createContext<GameSessionContextType | undefined>(undefined);

export const useGameSession = () => {
  const context = useContext(GameSessionContext);
  if (context === undefined) {
    throw new Error('useGameSession must be used within a GameSessionProvider');
  }
  return context;
};

interface GameSessionProviderProps {
  children: ReactNode;
}

export const GameSessionProvider: React.FC<GameSessionProviderProps> = ({ children }) => {
  const [currentSession, setCurrentSession] = useState<GameSession | null>(null);
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    const loadStoredSession = () => {
      if (isAuthenticated) {
        const storedSession = localStorage.getItem(STORAGE_KEYS.GAME_SESSION);
        if (storedSession) {
          try {
            const session = JSON.parse(storedSession);
            if (session && session.isActive) {
              setCurrentSession(session);
            }
          } catch (error) {
            console.error('Failed to parse stored session:', error);
            localStorage.removeItem(STORAGE_KEYS.GAME_SESSION);
          }
        }
      } else {
        setCurrentSession(null);
        localStorage.removeItem(STORAGE_KEYS.GAME_SESSION);
      }
    };

    loadStoredSession();
  }, [isAuthenticated, user]);

  const startSession = async (gameType: string, notes?: string): Promise<GameSession> => {
    try {
      if (!user?.uid) {
        throw new Error('User not authenticated');
      }
      
      const sessionId = await FirebaseDatabaseService.createGameSession(user.uid, gameType);
      const session: GameSession = {
        sessionId,
        userId: user.uid,
        gameType,
        startTime: new Date().toISOString(),
        isActive: true,
        notes,
        createdAt: new Date().toISOString()
      };
      
      setCurrentSession(session);
      localStorage.setItem(STORAGE_KEYS.GAME_SESSION, JSON.stringify(session));
      return session;
    } catch (error) {
      console.error('Failed to start game session:', error);
      throw error;
    }
  };

  const endSession = async (): Promise<void> => {
    if (!currentSession) return;

    try {
      await FirebaseDatabaseService.endGameSession(currentSession.sessionId);
      setCurrentSession(null);
      localStorage.removeItem(STORAGE_KEYS.GAME_SESSION);
    } catch (error) {
      console.error('Failed to end game session:', error);
      throw error;
    }
  };

  const refreshSession = async (): Promise<void> => {
    if (!isAuthenticated || !user?.uid) return;

    try {
      const activeSessions = await FirebaseDatabaseService.getUserActiveSessions(user.uid);
      const activeSession = activeSessions.length > 0 ? activeSessions[0] : null;
      
      if (activeSession) {
        setCurrentSession(activeSession);
        localStorage.setItem(STORAGE_KEYS.GAME_SESSION, JSON.stringify(activeSession));
      } else {
        setCurrentSession(null);
        localStorage.removeItem(STORAGE_KEYS.GAME_SESSION);
      }
    } catch (error) {
      console.error('Failed to refresh session:', error);
    }
  };

  const getUserSessionStats = async (): Promise<any> => {
    if (!isAuthenticated || !user?.uid) return {};

    try {
      // Get user's session statistics from Firebase
      const activeSessions = await FirebaseDatabaseService.getUserActiveSessions(user.uid);
      return {
        totalActiveSessions: activeSessions.length,
        lastActivity: activeSessions.length > 0 ? activeSessions[0].startTime : null,
        userId: user.uid
      };
    } catch (error) {
      console.error('Failed to get session stats:', error);
      throw error;
    }
  };

  const value: GameSessionContextType = {
    currentSession,
    isSessionActive: !!currentSession?.isActive,
    startSession,
    endSession,
    refreshSession,
    getUserSessionStats,
  };

  return <GameSessionContext.Provider value={value}>{children}</GameSessionContext.Provider>;
};